import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import timeZonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type { CustomLocale, MatchCalendarFormat } from '../../interfaces';

import * as locales from './locales';

/**
 * Apply time zone when it is available otherwise it will return local timezone
 *
 * @param {(dayjs.Dayjs)} date - Date value to parse into specific timezone
 * @param {string} timeZone - To apply new timezone to the date
 * @return New instance of Dayjs with custom timezone or local timezone
 */
function applyTimezone(date: dayjs.Dayjs, timeZone: string): dayjs.Dayjs {
  if (!timeZone) return date;

  dayjs.extend(utc);
  dayjs.extend(timeZonePlugin);
  try {
    const dateWithTz = date.clone().tz(timeZone);
    return dateWithTz.isValid() ? dateWithTz : date;
  } catch (e) {
    console.warn(`Invalid timezone: ${timeZone}`, e);
    return date;
  }
}

export function dateFormat(date: string | number | Date | dayjs.Dayjs, format: string, timeZone?: string): string {
  return applyTimezone(dayjs(date), timeZone).format(format);
}

export function calendarDateFormat(
  date: string | number | Date | dayjs.Dayjs,
  format: MatchCalendarFormat,
  timeZone?: string,
): string {
  dayjs.extend(calendar);
  return applyTimezone(dayjs(date), timeZone).calendar(null, format);
}

/**
 * It will load the corresponding i18n locale according to the user's language
 *
 * @param {string} language - String code language
 * @param {CustomLocale} customLocale - Cusom dayJs locale
 */
export function loadDateLocale(language: string, customLocale?: CustomLocale): void {
  const locale = locales[language] ?? customLocale;
  if (!locale) return;

  dayjs.locale(locale);
}
